/**
 * Root component and screen routing:
 *   replay open         → ReplayScreen ("The Fog Lifts", after a war or from the film archive)
 *   observing           → ObserveScreen ("From the Heights": our nation fell, the war goes on)
 *   gameStore.view set  → GameScreen (driven by the net layer / single-player worker)
 *   lobbyStore.room set → LobbyRoom
 *   otherwise           → the menu flow in navStore
 */
import { useEffect, useRef } from 'react';
import { useGame } from '../game/store';
import { useLobby } from '../net/lobby';
import { useReplay } from '../replay/store';
import { SvgDefs } from '../ui/common/Panel';
import { GameScreen } from '../ui/game/GameScreen';
import { MainMenu } from '../ui/menu/MainMenu';
import { MenuShell } from '../ui/menu/MenuShell';
import { ObserveScreen } from '../ui/replay/ObserveScreen';
import { ReplayScreen } from '../ui/replay/ReplayScreen';
import { ArchiveScreen } from '../ui/screens/Archive';
import { FieldManualScreen } from '../ui/screens/FieldManual';
import { LobbyRoom } from '../ui/screens/LobbyRoom';
import { MultiplayerScreen } from '../ui/screens/Multiplayer';
import { ScenariosScreen } from '../ui/screens/Scenarios';
import { SettingsScreen } from '../ui/screens/Settings';
import { SinglePlayerSetup } from '../ui/screens/SinglePlayerSetup';
import { navStore, useNav } from './nav';
import { useUiScale } from './useUiScale';

function MenuFlow() {
  const screen = useNav((n) => n.screen);
  const inRoom = useLobby((l) => l.room !== null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || inRoom) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, select')) return;
      navStore.getState().go('menu');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inRoom]);

  if (inRoom) return <LobbyRoom />;
  switch (screen) {
    case 'singleplayer':
      return <SinglePlayerSetup key="sp" />;
    case 'scenarios':
      return <ScenariosScreen key="scenarios" />;
    case 'multiplayer':
      return <MultiplayerScreen key="mp" />;
    case 'archive':
      return <ArchiveScreen key="archive" />;
    case 'manual':
      return <FieldManualScreen key="manual" />;
    case 'settings':
      return <SettingsScreen key="settings" />;
    default:
      return <MainMenu key="menu" />;
  }
}

export function App() {
  useUiScale();
  const inGame = useGame((s) => s.view !== null);
  const observing = useGame((s) => s.observing !== null);
  const replay = useReplay((r) => r.active);

  // Coming back from a match lands on the title screen, not the setup form that launched it. A
  // film from the archive is not a match: closing it returns to the archive.
  const inMatch = inGame && !replay;
  const wasInMatch = useRef(inMatch);
  useEffect(() => {
    if (wasInMatch.current && !inMatch) navStore.getState().go('menu');
    wasInMatch.current = inMatch;
  }, [inMatch]);

  return (
    <>
      <SvgDefs />
      {replay ? (
        <ReplayScreen />
      ) : inGame && observing ? (
        <ObserveScreen />
      ) : inGame ? (
        <GameScreen />
      ) : (
        <MenuShell>
          <MenuFlow />
        </MenuShell>
      )}
    </>
  );
}
